<template>
  <!-- Every key the player answers to. Opened with "?" or the keyboard button; playback waits while it
       is up, and only its own two keys work, so reading it changes nothing behind it. -->
  <transition name="vhelp-menu">
    <div
      v-if="open"
      ref="rootRef"
      class="vhelp__shortcuts"
      role="dialog"
      tabindex="-1"
      :aria-label="t('help.shortcuts.title')"
      @keydown="onKeydown"
    >
      <div class="vhelp__shortcuts-head">
        <span class="vhelp__shortcuts-title">{{ t("help.shortcuts.title") }}</span>
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          density="comfortable"
          :aria-label="t('help.shortcuts.close')"
          @click="emit('close', true)"
        />
      </div>
      <dl class="vhelp__shortcuts-list">
        <div v-for="row in rows" :key="row.key" class="vhelp__shortcuts-row">
          <dt class="vhelp__shortcuts-keys">
            <template v-for="(token, position) in row.tokens" :key="position">
              <kbd v-if="token.kbd">{{ token.kbd }}</kbd>
              <span v-else class="vhelp__shortcuts-join">{{ token.text }}</span>
            </template>
          </dt>
          <dd>{{ row.label }}</dd>
        </div>
      </dl>
    </div>
  </transition>
</template>

<script setup lang="ts">
/**
 * The list of the walkthrough's keyboard shortcuts — a dialog inside the dialog. The keys themselves
 * are `useViewHelpKeyboard`'s; this lists them, built from the same facts they act on.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue"
import { useViewHelp } from "@/composables/viewHelp/useViewHelp"
import { useViewHelpCopy } from "@/composables/viewHelp/useViewHelpCopy"

const props = defineProps<{ open: boolean }>()

const emit = defineEmits<{
  /** `returnFocus`: put focus back where it was before the list opened. */
  close: [returnFocus: boolean]
}>()

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const { sections } = useViewHelp()
const { t } = useViewHelpCopy()

const rootRef = ref<HTMLElement | null>(null)

type Token = { kbd: string; text?: never } | { text: string; kbd?: never }

/** The view rows only appear when there is more than one view, and the digit row says how many. */
const rows = computed(() => {
  const k = (kbd: string): Token => ({ kbd })
  const views = sections.value.length
  const list: { key: string; tokens: Token[]; label: string }[] = [
    { key: "play", tokens: [k(t("help.keys.space"))], label: t("help.shortcuts.play") },
    { key: "step", tokens: [k("←"), k("→")], label: t("help.shortcuts.step") },
  ]
  if (views > 1) {
    list.push(
      {
        key: "view",
        tokens: [k(t("help.keys.shift")), { text: "+" }, k("←"), k("→")],
        label: t("help.shortcuts.view"),
      },
      { key: "jump", tokens: [k("1"), { text: "–" }, k(String(Math.min(9, views)))], label: t("help.shortcuts.jump") }
    )
  }
  list.push(
    { key: "edges", tokens: [k(t("help.keys.home")), k(t("help.keys.end"))], label: t("help.shortcuts.edges") },
    { key: "speed", tokens: [k("<"), k(">")], label: t("help.shortcuts.speed") },
    { key: "list", tokens: [k("?")], label: t("help.shortcuts.list") },
    { key: "close", tokens: [k("Esc")], label: t("help.shortcuts.exit") }
  )
  return list
})

/** Tab stays inside the list while it is open. */
function onKeydown(event: KeyboardEvent) {
  if (event.key !== "Tab") return
  event.stopPropagation()
  const focusable = Array.from(rootRef.value?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
  if (!focusable.length) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

/**
 * A press anywhere else closes the list — except on the keyboard button, which toggles it: closing it
 * here first would have that button's click open it straight back up.
 */
function onPressOutside(event: MouseEvent) {
  const target = event.target as HTMLElement | null
  if (rootRef.value?.contains(target) || target?.closest(".vhelp__shortcuts-toggle")) return
  emit("close", false)
}

watch(
  () => props.open,
  async open => {
    if (!open) {
      document.removeEventListener("mousedown", onPressOutside, true)
      return
    }
    document.addEventListener("mousedown", onPressOutside, true)
    await nextTick()
    rootRef.value?.focus()
  }
)

onBeforeUnmount(() => document.removeEventListener("mousedown", onPressOutside, true))
</script>

<style scoped>
/* Over the stage and the player alike (both sit at 5), centred in the dialog. */
.vhelp__shortcuts {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 8;
  width: min(420px, calc(100% - 24px));
  max-height: calc(100% - 24px);
  overflow-y: auto;
  padding: 10px 14px 14px;
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  transform: translate(-50%, -50%);
  outline: none;
}

.vhelp-menu-enter-active,
.vhelp-menu-leave-active {
  transition:
    opacity 0.14s ease,
    transform 0.14s ease;
}

.vhelp-menu-enter-from,
.vhelp-menu-leave-to {
  opacity: 0;
  transform: translate(-50%, calc(-50% + 4px));
}

.vhelp__shortcuts-head {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.vhelp__shortcuts-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.65;
}

.vhelp__shortcuts-list {
  margin: 0;
}

.vhelp__shortcuts-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 0;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  font-size: 0.82rem;
}

.vhelp__shortcuts-keys {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: none;
  width: 9.5em;
}

.vhelp__shortcuts-keys kbd {
  min-width: 1.9em;
  padding: 2px 6px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
  border-bottom-width: 2px;
  border-radius: 5px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  color: inherit;
  font-family: inherit;
  font-size: 0.75rem;
  line-height: 1.4;
  text-align: center;
}

.vhelp__shortcuts-join {
  font-size: 0.75rem;
  opacity: 0.6;
}

.vhelp__shortcuts-row dd {
  margin: 0;
}
</style>
